import {
    convivaAppTracker,
    trackPageView,
    setUserId,
} from '@convivainc/conviva-js-appanalytics';
import { boot } from '@dazn/bootstrap-lib';
import { UserToken } from '@dazn/bootstrap-lib/dist/boot/user';

import { GTMEvent } from '../analytics/handleCommonFeEvent';
import { appendScriptBySource } from '../cookieBanner/appendScript';

import {
    BRAZE_FLAG,
    CONVIVA_WEB_SDK_FLAG,
    FULLSTORY_FLAG,
} from '../fflag/constants';

export interface CreateMonitoringModuleProps {
    isEnabledAnonymous: (
        experimentName: string,
        customAttrs?: boot.fflag.CustomAttributes | undefined,
    ) => boolean | null;
    getVariableStringAnonymous: (
        experimentKey: string,
        variableKey: string,
        customAttrs?: boot.fflag.CustomAttributes | undefined,
    ) => string | null;
    isProduction: boolean;
}

export const createMonitoringModule = ({
    isEnabledAnonymous,
    getVariableStringAnonymous,
    isProduction,
}: CreateMonitoringModuleProps) => {
    let isFullStoryEnabled: boolean | null;
    let isBrazeEnabled: boolean | null;
    let brazeApiKey: string;
    let brazeBaseUrl: string;
    let isConvivaWebSdkEnabled: boolean | null;
    let excludedEventIds: string[] = [];
    const initializationScript = (
        host: string,
        scriptUrl: string,
        orgId: string,
    ) => `
    window['_fs_host'] = '${host}';
    window['_fs_script'] = '${scriptUrl}';
    window['_fs_org'] = '${orgId}';
    window['_fs_namespace'] = 'FS';
    `;
    const fullStoryScript = `!function(m,n,e,t,l,o,g,y){var s,f,a=function(h){
        return!(h in m)||(m.console&&m.console.log&&m.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].'),!1)}(e)
        ;function p(b){var h,d=[];function j(){h&&(d.forEach((function(b){var d;try{d=b[h[0]]&&b[h[0]](h[1])}catch(h){return void(b[3]&&b[3](h))}
        d&&d.then?d.then(b[2],b[3]):b[2]&&b[2](d)})),d.length=0)}function r(b){return function(d){h||(h=[b,d],j())}}return b(r(0),r(1)),{
        then:function(b,h){return p((function(r,i){d.push([b,h,r,i]),j()}))}}}a&&(g=m[e]=function(){var b=function(b,d,j,r){function i(i,c){
        h(b,d,j,i,c,r)}r=r||2;var c,u=/Async$/;return u.test(b)?(b=b.replace(u,""),"function"==typeof Promise?new Promise(i):p(i)):h(b,d,j,c,c,r)}
        ;function h(h,d,j,r,i,c){return b._api?b._api(h,d,j,r,i,c):(b.q&&b.q.push([h,d,j,r,i,c]),null)}return b.q=[],b}(),y=function(b){function h(h){
        "function"==typeof h[4]&&h[4](new Error(b))}var d=g.q;if(d){for(var j=0;j<d.length;j++)h(d[j]);d.length=0,d.push=h}},function(){
        (o=n.createElement(t)).async=!0,o.crossOrigin="anonymous",o.src="https://"+l,o.onerror=function(){y("Error loading "+l)}
        ;var b=n.getElementsByTagName(t)[0];b&&b.parentNode?b.parentNode.insertBefore(o,b):n.head.appendChild(o)}(),function(){function b(){}
        function h(b,h,d){g(b,h,d,1)}function d(b,d,j){h("setProperties",{type:b,properties:d},j)}function j(b,h){d("user",b,h)}function r(b,h,d){j({
        uid:b},d),h&&j(h,d)}g.identify=r,g.setUserVars=j,g.identifyAccount=b,g.clearUserCookie=b,g.setVars=d,g.event=function(b,d,j){h("trackEvent",{
        name:b,properties:d},j)},g.anonymize=function(){r(!1)},g.shutdown=function(){h("shutdown")},g.restart=function(){h("restart")},
        g.log=function(b,d){h("log",{level:b,msg:d})},g.consent=function(b){h("setIdentity",{consent:!arguments.length||b})}}(),s="fetch",
        f="XMLHttpRequest",g._w={},g._w[f]=m[f],g._w[s]=m[s],m[s]&&(m[s]=function(){return g._w[s].apply(this,arguments)}),g._v="2.0.0")
        }(window,document,window._fs_namespace,"script",window._fs_script);`;

    // Function to trigger identify for multiple monitoring modules
    const triggerIdenity = (token: UserToken | null) => {
        // Fullstory Identity
        if (isFullStoryEnabled) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            window.FS('setIdentity', {
                uid: token?.user,
                properties: {
                    displayName: token?.user,
                    geoCountry: token?.country,
                    contentCountry: token?.contentCountry,
                    homeCountry: token?.homeCountry,
                    viewerId: token?.viewerId,
                },
            });
        }
        if (isConvivaWebSdkEnabled) {
            setUserId(token?.user);
        }
    };

    const identifyCurrentUser = () => {
        const token = window.dazn.auth.unstable_getDecodedJWT();
        const currentActiveProfile = window.dazn.userProfile?.profiles?.find(
            profile => profile?.viewerId === window.dazn.userProfile.ViewerId,
        );
        return currentActiveProfile?.isRootProfile
            ? token?.user
            : currentActiveProfile?.viewerId;
    };

    const userProfileNotSelected: boolean =
        window.localStorage.getItem('chooseYourProfile') === 'true' ||
        window.sessionStorage.getItem('chooseYourProfile') === 'true';

    //Function to initialise Braze and set user profile
    const initialiseBrazeAndSetUserProfile = (
        apiKey: string,
        baseUrl: string,
    ) => {
        // initialize the SDK
        import('@braze/web-sdk').then(braze => {
            if (!braze.isInitialized()) {
                braze.initialize(apiKey, {
                    baseUrl: baseUrl,
                    enableLogging: !isProduction,
                    allowUserSuppliedJavascript: true,
                });
            }
            // Call changeuser only if profile has been selected on catalog, else wait for chapter to call updateBrazeUserProfile
            if (!userProfileNotSelected) {
                braze.changeUser(identifyCurrentUser() || '');
            }
            braze.automaticallyShowInAppMessages();
            braze.openSession();
        });
    };

    return {
        run: () => {
            //Fullstory section begins
            isFullStoryEnabled = isEnabledAnonymous(FULLSTORY_FLAG);
            const host =
                getVariableStringAnonymous(FULLSTORY_FLAG, 'host') ??
                'eu1.fullstory.com';

            const scriptUrl =
                getVariableStringAnonymous(FULLSTORY_FLAG, 'scriptUrl') ??
                'edge.eu1.fullstory.com/s/fs.js';

            const orgId =
                getVariableStringAnonymous(FULLSTORY_FLAG, 'orgId') ??
                'o-26WE-eu1';

            const token = window.dazn.auth.unstable_getDecodedJWT();

            try {
                excludedEventIds = JSON.parse(
                    getVariableStringAnonymous(
                        FULLSTORY_FLAG,
                        'excludedEventIds',
                    ) ?? '',
                );
            } catch (error) {
                // Do nothing
            }

            if (isFullStoryEnabled) {
                appendScriptBySource(
                    initializationScript(host, scriptUrl, orgId),
                    {},
                    true,
                );
                appendScriptBySource(fullStoryScript, {}, false);
            }
            //Fullstory section ends

            //braze section begins
            isBrazeEnabled = isEnabledAnonymous(BRAZE_FLAG);

            brazeApiKey =
                getVariableStringAnonymous(BRAZE_FLAG, 'apiKey') ??
                'f5303fed-aa46-427e-bfa7-a8d92d8e7e75';

            brazeBaseUrl =
                getVariableStringAnonymous(BRAZE_FLAG, 'baseUrl') ??
                'sdk.iad-01.braze.com';
            //braze section ends

            //Conviva JS web SDK section begins
            isConvivaWebSdkEnabled = isEnabledAnonymous(CONVIVA_WEB_SDK_FLAG);
            const convivaKey =
                getVariableStringAnonymous(
                    CONVIVA_WEB_SDK_FLAG,
                    'customerKey',
                ) ?? '4ae1981d8d1a370d08bf3d24f50f5baf47ef1b62';
            if (isConvivaWebSdkEnabled) {
                try {
                    convivaAppTracker({
                        appId: 'WEB_BOOTSTRAP',
                        convivaCustomerKey: convivaKey,
                    });
                    trackPageView();
                } catch (error) {
                    // Do nothing. Safety check to make sure if conviva breaks app is not affected.
                }
            }
            //Conviva JS web SDK section ends

            // Braze section begins
            if (isBrazeEnabled) {
                if (Boolean(token)) {
                    initialiseBrazeAndSetUserProfile(brazeApiKey, brazeBaseUrl);
                }
            }
            // Braze section ends

            const delay = 1000;
            setTimeout(() => {
                if (Boolean(token)) {
                    triggerIdenity(token);
                }
            }, delay);
        },

        triggerFsEvent: (eventPayload: GTMEvent) => {
            if (
                isFullStoryEnabled &&
                eventPayload &&
                eventPayload.event &&
                excludedEventIds.indexOf(eventPayload.event) === -1
            ) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                window.FS('trackEvent', {
                    name: eventPayload?.event,
                    properties: eventPayload,
                });
            }
            return Promise.resolve();
        },

        triggerIdentify: () => {
            triggerIdenity(window.dazn.auth.unstable_getDecodedJWT());
        },

        updateBrazeUserProfile: () => {
            if (isBrazeEnabled && Boolean(window.dazn?.auth?.token)) {
                initialiseBrazeAndSetUserProfile(brazeApiKey, brazeBaseUrl);
            }
        },
    };
};
